/**
 * 이상한 방 - 시드 기반 결정론 PRNG
 *
 * 근거: 스피드런 / 일일 챌린지. **같은 시드는 같은 방을 만들어야 한다.**
 * 두 사람이 같은 챌린지를 돌았는데 방이 다르면 기록 비교가 성립하지 않는다.
 *
 * 그래서 이 프로젝트에서 `Math.random()` 사용은 전면 금지다.
 * 절차적 생성 경로(roomGen)는 물론 CPU 상대(설계문서 9절)의 행동 선택까지
 * 전부 이 모듈을 거쳐야 리플레이·버그 재현이 가능해진다.
 *
 * ## 알고리즘 선택
 *
 * - **문자열/숫자 시드 → 32bit 정수**: `cyrb128` 해시. 인접한 시드('room-1'과 'room-2',
 *   41과 42)가 비슷한 수열을 내지 않도록 눈사태 효과가 있는 해시가 필요하다.
 *   단순히 문자 코드를 더하면 'ab'와 'ba'가 같은 방을 만든다.
 * - **수열 생성 → `mulberry32`**: 32bit 상태 하나짜리 소형 PRNG.
 *   주기 2^32이며 게임 한 판이 소비하는 수(수만 개)에 비해 충분히 길다.
 *   암호학적 안전성은 요구사항이 아니다 - 재현성이 요구사항이다.
 *
 * ## 부동소수점 재현성
 *
 * `Math.imul`·비트 연산·`>>> 0`만 쓰므로 정수 경로에서 나눗셈 한 번(`/ 2^32`)만 부동소수점이다.
 * IEEE 754 하에서 2의 거듭제곱 나눗셈은 정확하므로 플랫폼 간 결과가 갈리지 않는다.
 * `Math.pow`·`Math.sin` 같은 초월함수 기반 PRNG를 피한 이유가 이것이다 -
 * 그런 함수는 구현체마다 마지막 비트가 다를 수 있고, 그러면 서버와 클라이언트의 방이 갈린다.
 */

/** 32bit 부호 없는 정수의 개수. `next()`가 [0, 1)로 정규화할 때 쓰는 나눗수. */
const UINT32_RANGE = 4294967296

/* --- cyrb128: 문자열 → 128bit 해시 (알고리즘 고정 상수) ------------------------- */

/** 4워드 상수 묶음. 튜플로 고정해 인덱스 접근이 `undefined`가 되지 않게 한다. */
type Quad = readonly [number, number, number, number]

/** 해시 초기 상태 4워드. 값 자체에 의미는 없고 서로 다른 홀수 상수라는 점만 중요하다. */
const CYRB128_INIT: Quad = [1779033703, 3144134277, 1013904242, 2773480762]

/** 라운드마다 곱하는 상수 4개. 눈사태 효과(1비트 변화가 전체로 퍼짐)를 만드는 부분이다. */
const CYRB128_MUL: Quad = [597399067, 2869860233, 951274213, 2716044179]

/** 최종 혼합 단계의 우측 시프트량. 상위 비트를 하위로 되먹여 편향을 없앤다. */
const CYRB128_SHIFT: Quad = [18, 22, 17, 19]

/* --- mulberry32: 32bit 상태 PRNG (알고리즘 고정 상수) --------------------------- */

const MULBERRY_INCREMENT = 0x6d2b79f5
const MULBERRY_SHIFT_A = 15
const MULBERRY_SHIFT_B = 7
const MULBERRY_SHIFT_C = 14
const MULBERRY_OR_A = 1
const MULBERRY_OR_B = 61

/**
 * 시드 기반 난수원. 모든 메서드는 호출당 정확히 정해진 개수의 난수를 소비한다.
 *
 * 소비량이 고정이라는 점이 계약의 일부다 - `chance(0)`처럼 결과가 뻔한 경우에도
 * 난수를 소비하지 않으면, 확률값만 바꿨는데 그 뒤 수열 전체가 어긋나 방이 통째로 달라진다.
 */
export type Rng = Readonly<{
  /** [0, 1) 구간의 다음 난수. 다른 모든 메서드가 이 위에 얹혀 있다. */
  next(): number
  /** [min, max] **양끝 포함** 정수. min > max이거나 정수가 아니면 던진다. */
  int(min: number, max: number): number
  /** 배열에서 하나를 고른다. 빈 배열은 호출자의 버그이므로 던진다. */
  pick<T>(items: readonly T[]): T
  /** Fisher-Yates 셔플. **원본을 건드리지 않고 새 배열을 반환한다.** */
  shuffle<T>(items: readonly T[]): T[]
  /** 확률 p로 true. p는 [0, 1] 범위여야 하며 항상 난수 1개를 소비한다. */
  chance(p: number): boolean
  /** 가중치 기반 선택. 가중치는 0 이상이어야 하고 합이 0보다 커야 한다. */
  weighted<T>(items: readonly T[], weights: readonly number[]): T
}>

/**
 * 문자열을 128bit로 해싱한다. 반환 4워드 중 첫 워드만 PRNG 시드로 쓰지만,
 * 4워드를 전부 계산하는 것이 알고리즘 정의이며 마지막 혼합 단계가 편향 제거를 담당한다.
 */
function cyrb128(text: string): number {
  let h1 = CYRB128_INIT[0]
  let h2 = CYRB128_INIT[1]
  let h3 = CYRB128_INIT[2]
  let h4 = CYRB128_INIT[3]

  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i)
    h1 = h2 ^ Math.imul(h1 ^ code, CYRB128_MUL[0])
    h2 = h3 ^ Math.imul(h2 ^ code, CYRB128_MUL[1])
    h3 = h4 ^ Math.imul(h3 ^ code, CYRB128_MUL[2])
    h4 = h1 ^ Math.imul(h4 ^ code, CYRB128_MUL[3])
  }

  h1 = Math.imul(h3 ^ (h1 >>> CYRB128_SHIFT[0]), CYRB128_MUL[0])
  h2 = Math.imul(h4 ^ (h2 >>> CYRB128_SHIFT[1]), CYRB128_MUL[1])
  h3 = Math.imul(h1 ^ (h3 >>> CYRB128_SHIFT[2]), CYRB128_MUL[2])
  h4 = Math.imul(h2 ^ (h4 >>> CYRB128_SHIFT[3]), CYRB128_MUL[3])

  return (h1 ^ h2 ^ h3 ^ h4) >>> 0
}

/** 32bit 상태 PRNG 본체. 상태를 클로저에 가둬 두 인스턴스가 서로 간섭하지 못하게 한다. */
function mulberry32(initialState: number): () => number {
  let state = initialState | 0

  return function nextUint32(): number {
    state = (state + MULBERRY_INCREMENT) | 0
    let mixed = Math.imul(state ^ (state >>> MULBERRY_SHIFT_A), MULBERRY_OR_A | state)
    mixed =
      (mixed + Math.imul(mixed ^ (mixed >>> MULBERRY_SHIFT_B), MULBERRY_OR_B | mixed)) ^ mixed
    return (mixed ^ (mixed >>> MULBERRY_SHIFT_C)) >>> 0
  }
}

/**
 * 시드를 32bit 정수로 정규화한다.
 *
 * 숫자 시드도 문자열로 만들어 같은 해시를 태운다. 경로를 하나로 합친 이유는 두 가지다:
 *  1. 숫자를 그대로 상태로 쓰면 시드 1·2·3이 서로 이웃한 상태가 되어 초기 몇 수가 비슷해진다.
 *  2. 코드 경로가 하나면 "숫자 시드에서만 나는 버그"가 존재할 수 없다.
 *
 * 부수 효과로 `createRng(42)`와 `createRng('42')`는 **같은 수열**을 만든다. 의도된 동작이다.
 */
function normalizeSeed(seed: number | string): number {
  if (typeof seed === 'number') {
    if (!Number.isFinite(seed)) {
      throw new RangeError(`시드가 유한수가 아니다: ${String(seed)}`)
    }
    return cyrb128(String(seed))
  }
  return cyrb128(seed)
}

function assertInteger(label: string, value: number): void {
  if (Number.isInteger(value)) return
  throw new RangeError(`${label}는 정수여야 한다: ${String(value)}`)
}

/**
 * 시드로부터 난수원을 만든다. 같은 시드 → 항상 같은 수열이다(이 모듈의 존재 이유).
 *
 * 인스턴스끼리 상태를 공유하지 않으므로, 방 생성용과 CPU 행동용을 분리하고 싶으면
 * `createRng(`${seed}:gen`)` / `createRng(`${seed}:cpu`)` 처럼 시드에 접미사를 붙이면 된다.
 * 한 인스턴스를 돌려 쓰면 한쪽 호출 횟수가 바뀔 때 다른 쪽 결과까지 흔들린다.
 */
export function createRng(seed: number | string): Rng {
  const nextUint32 = mulberry32(normalizeSeed(seed))

  const next = (): number => nextUint32() / UINT32_RANGE

  const int = (min: number, max: number): number => {
    assertInteger('int의 min', min)
    assertInteger('int의 max', max)
    if (min > max) {
      throw new RangeError(`int의 min이 max보다 크다: min=${min}, max=${max}`)
    }

    const span = max - min + 1
    const value = min + Math.floor(next() * span)
    // next()가 1 미만이므로 이론상 도달할 수 없지만, 부동소수점 반올림 한 번이면
    // 범위를 벗어난 인덱스가 생기고 그 자리에서 배열 접근이 undefined가 된다. 싸게 막는다.
    return value > max ? max : value
  }

  const pick = <T>(items: readonly T[]): T => {
    if (items.length === 0) {
      throw new RangeError('pick: 빈 배열에서는 고를 수 없다')
    }
    const chosen = items[int(0, items.length - 1)]
    if (chosen === undefined) {
      throw new RangeError('pick: 배열에 구멍(hole)이 있다')
    }
    return chosen
  }

  const shuffle = <T>(items: readonly T[]): T[] => {
    // 원본을 제자리 섞으면 카탈로그 같은 공유 배열이 조용히 오염된다. 항상 복사본을 섞는다.
    const result = [...items]
    for (let i = result.length - 1; i > 0; i -= 1) {
      const j = int(0, i)
      const a = result[i]
      const b = result[j]
      // noUncheckedIndexedAccess 하에서의 형식 서술. i·j 모두 범위 안이라 실제로는 발생하지 않는다.
      if (a === undefined || b === undefined) continue
      result[i] = b
      result[j] = a
    }
    return result
  }

  const chance = (p: number): boolean => {
    if (!Number.isFinite(p) || p < 0 || p > 1) {
      throw new RangeError(`chance의 확률은 0~1이어야 한다: ${String(p)}`)
    }
    // p가 0이나 1이어도 난수를 소비한다. 소비량이 흔들리면 그 뒤 수열 전체가 어긋난다.
    return next() < p
  }

  const weighted = <T>(items: readonly T[], weights: readonly number[]): T => {
    if (items.length === 0) {
      throw new RangeError('weighted: 빈 배열에서는 고를 수 없다')
    }
    if (items.length !== weights.length) {
      throw new RangeError(
        `weighted: items와 weights의 길이가 다르다 (${items.length} vs ${weights.length})`,
      )
    }

    let total = 0
    for (const weight of weights) {
      if (!Number.isFinite(weight) || weight < 0) {
        throw new RangeError(`weighted: 가중치는 0 이상의 유한수여야 한다: ${String(weight)}`)
      }
      total += weight
    }
    if (total <= 0) {
      throw new RangeError('weighted: 가중치 합이 0보다 커야 한다')
    }

    const threshold = next() * total
    let cumulative = 0
    for (let i = 0; i < items.length; i += 1) {
      cumulative += weights[i] ?? 0
      if (threshold < cumulative) {
        const chosen = items[i]
        if (chosen !== undefined) return chosen
      }
    }

    // 부동소수점 누적 오차로 threshold가 마지막 경계를 넘긴 경우의 폴백.
    // 가중치가 0인 항목이 마지막이면 그것을 고르게 되므로, 뒤에서부터 양수 가중치를 찾는다.
    for (let i = items.length - 1; i >= 0; i -= 1) {
      if ((weights[i] ?? 0) > 0) {
        const chosen = items[i]
        if (chosen !== undefined) return chosen
      }
    }
    throw new RangeError('weighted: 양수 가중치를 가진 항목을 찾지 못했다')
  }

  return { next, int, pick, shuffle, chance, weighted }
}
